import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Camera,
  Focus,
  Sliders,
  Grid,
  Video,
  Settings
} from 'lucide-react';
import LegalLayout from '../LegalLayout';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const accentColor = '#8b5cf6';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>
      {isOpen && (
        <div
          className="px-6 pb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const accentColor = '#8b5cf6';
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      icon: <Camera className="w-5 h-5" />,
      question: "How do I grant camera permissions?",
      answer: (
        <div className="space-y-2">
          <p>To grant Prismatica camera access:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open <strong>Settings</strong> on your iPhone/iPad</li>
            <li>Scroll down and tap <strong>Prismatica</strong></li>
            <li>Enable <strong>Camera</strong> toggle</li>
            <li>For video recording, also enable <strong>Microphone</strong></li>
            <li>Enable <strong>Photos</strong> to save your captures</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Note: Camera permission is required for the app to function. You'll be prompted on first launch.
          </p>
        </div>
      )
    },
    {
      icon: <Focus className="w-5 h-5" />,
      question: "How does tap-to-focus work?",
      answer: (
        <div className="space-y-2">
          <p>Prismatica provides intuitive tap-to-focus functionality:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Tap anywhere on the camera preview to focus on that point</li>
            <li>A yellow focus indicator will appear showing the focus area</li>
            <li>The camera will automatically adjust exposure for that region</li>
            <li>Focus locks briefly then returns to continuous autofocus</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Tip: For manual focus control, open the Focus Tools panel from the toolbar.
          </p>
        </div>
      )
    },
    {
      icon: <Sliders className="w-5 h-5" />,
      question: "How do I use the floating panels?",
      answer: (
        <div className="space-y-2">
          <p>Prismatica features customizable floating panels for quick access to controls:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Camera Controls:</strong> Adjust exposure, ISO, white balance</li>
            <li><strong>Focus Tools:</strong> Manual focus and focus peaking</li>
            <li><strong>Performance:</strong> View frame rate, thermal state, battery</li>
            <li><strong>Filters:</strong> Apply real-time photo filters</li>
          </ul>
          <p className="mt-3"><strong>To enable floating panels:</strong></p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Tap the toolbar at the bottom of the screen</li>
            <li>Select a panel category to expand it</li>
            <li>Tap the float icon to enable draggable floating panels</li>
            <li>Drag panels anywhere on screen for your preferred layout</li>
          </ol>
        </div>
      )
    },
    {
      icon: <Video className="w-5 h-5" />,
      question: "What video formats are supported?",
      answer: (
        <div className="space-y-2">
          <p>Prismatica supports multiple video formats:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>MOV:</strong> Apple's native format with best quality</li>
            <li><strong>MP4:</strong> Widely compatible format for sharing</li>
            <li><strong>M4V:</strong> iTunes-compatible video format</li>
          </ul>
          <p className="mt-3"><strong>To change video format:</strong></p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open Settings (gear icon)</li>
            <li>Go to the Capture section</li>
            <li>Select your preferred Video Format</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Note: ProRes and higher quality formats may require devices with advanced video capabilities.
          </p>
        </div>
      )
    },
    {
      icon: <Grid className="w-5 h-5" />,
      question: "How do I enable the grid overlay?",
      answer: (
        <div className="space-y-2">
          <p>The grid overlay helps with composition using the rule of thirds:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open Settings (gear icon in the top bar)</li>
            <li>Find the Display section</li>
            <li>Toggle on <strong>Grid Overlay</strong></li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Tip: Use the grid lines to align horizons and position subjects at intersection points for more balanced compositions.
          </p>
        </div>
      )
    },
    {
      icon: <Settings className="w-5 h-5" />,
      question: "How do I change the photo format?",
      answer: (
        <div className="space-y-2">
          <p>Prismatica supports multiple photo formats:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>JPEG:</strong> Universally compatible, smaller file size</li>
            <li><strong>HEIC:</strong> Apple's efficient format with better quality/size ratio</li>
            <li><strong>PNG:</strong> Lossless format for maximum quality</li>
            <li><strong>DNG:</strong> RAW format for professional editing</li>
          </ul>
          <p className="mt-3"><strong>To change photo format:</strong></p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open Settings (gear icon)</li>
            <li>Navigate to the Capture section</li>
            <li>Select your preferred Photo Format</li>
          </ol>
        </div>
      )
    }
  ];

  return (
    <LegalLayout app="Prismatica" docType="Support" icon={HelpCircle}>
        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="mailto:tanushgupta@feraltechlabs.co.in"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Mail className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Email Support
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  tanushgupta@feraltechlabs.co.in
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              We typically respond within 24-48 hours
            </p>
          </a>

          <a
            href="mailto:tanushgupta@feraltechlabs.co.in?subject=Prismatica%20Bug%20Report"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Report a Bug
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Help us improve Prismatica
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Include your device model and iOS version
            </p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* App Info */}
        <div
          className="rounded-xl p-6"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            App Information
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>App Name:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Prismatica</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Developer:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Feral Techlab</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Platform:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>iOS 16.0+</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Category:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Photo & Video</span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link
            to="/apps/prismatica/privacy"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Privacy Policy
          </Link>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <Link
            to="/apps/prismatica/terms"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Terms & Conditions
          </Link>
        </div>
    </LegalLayout>
  );
};

export default Support;
